/* eslint-disable max-statements */

import { Button, Group, Loader, Stack, Title } from '@mantine/core';
import { range } from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import { Select } from '@components';
import { VAT_REPORT_MONTHS_IN_YEAR, VAT_REPORT_QUARTERS_IN_YEAR, VAT_REPORT_YEAR_HISTORY_COUNT } from '@constants';

interface VatReportProps {
  downloadReport: {
    mutate: (criteria: { year: number; month: number | null; quarter: number | null }) => void;
  };
  title: string;
}

export const VatReport: React.FC<VatReportProps> = ({ title, downloadReport }) => {
  const months = useRef(
    range(VAT_REPORT_MONTHS_IN_YEAR).map((index) => ({
      label: moment(`${index + 1}-01-2023`).format('MMMM'),
      value: `${index + 1}`,
    })),
  );
  const quarters = useRef(
    range(VAT_REPORT_QUARTERS_IN_YEAR).map((index) => ({
      label: `Q${index + 1}`,
      value: `${index + 1}`,
    })),
  );

  const years = useRef(
    range(VAT_REPORT_YEAR_HISTORY_COUNT).map((index) => ({
      label: moment(`01-01-${moment().year() - index}`).format('YYYY'),
      value: `${moment().year() - index}`,
    })),
  );

  const [selectedMonth, setSelectedMonth] = useState('0');
  const [selectedQuarter, setSelectedQuarter] = useState('0');
  const [selectedYear, setSelectedYear] = useState('0');

  const [monthMode, setMonthMode] = useState(true);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const currentMonth = moment().month();
    const currentQuarter = moment().quarter() - 1;

    setSelectedMonth(months.current[currentMonth].value);
    setSelectedQuarter(quarters.current[currentQuarter].value);
    setSelectedYear(years.current[0].value);
  }, []);

  const monthModeClickHandler = () => {
    setMonthMode(!monthMode);
  };

  const monthChangeHandler = (selectedOption) => {
    setSelectedMonth(selectedOption);
  };

  const quarterChangeHandler = (selectedOption) => {
    setSelectedQuarter(selectedOption);
  };

  const yearChangeHandler = (selectedOption) => {
    setSelectedYear(selectedOption);
  };

  const handleDownloadReport = async () => {
    setIsLoading(true);
    downloadReport.mutate({
      year: Number(selectedYear),
      month: monthMode ? Number(selectedMonth) : null,
      quarter: !monthMode ? Number(selectedQuarter) : null,
    });
    setIsLoading(false);
  };

  return (
    <Stack gap="xl">
      <Title order={2}>{title}</Title>

      <Group>
        <Button variant="outline" onClick={monthModeClickHandler}>
          {monthMode ? 'Use quarter' : 'Use month'}
        </Button>
      </Group>

      {monthMode ? (
        <Select label="Select a month" onChange={monthChangeHandler} data={months.current} value={selectedMonth} />
      ) : (
        <Select label="Select a quarter" onChange={quarterChangeHandler} data={quarters.current} value={selectedQuarter} />
      )}

      <Select label="Select a year" onChange={yearChangeHandler} data={years.current} value={selectedYear} />

      <Group>
        <Button leftSection={isLoading && <Loader color="#FFFFFF" />} onClick={handleDownloadReport} disabled={isLoading} variant="filled" size="lg">
          {isLoading ? 'Generating report..' : 'Generate report'}
        </Button>
      </Group>
    </Stack>
  );
};
